// Library Methods
import i18next from "i18next";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import { PlayIcon } from "src/assets/icons";

// Styles
import { UI_SIZES } from "src/constants/uiTokens";
import { getSurfaceTokens } from "src/styles/mui/muiStylesHelper";

// Types
import { TutorialVideoType } from "src/types/sharedTypes";

// Config
import { priorityLanguageMap } from "src/config/languages";

type VideoCardType = {
  tutorialVideo: TutorialVideoType;
  selectedVideo: TutorialVideoType;
  handleSelectVideo: (video: TutorialVideoType) => void;
};

const VideoCard = ({
  tutorialVideo,
  selectedVideo,
  handleSelectVideo,
}: VideoCardType) => {
  const { t } = useTranslation();
  const currentLang = i18next.language as keyof typeof priorityLanguageMap;
  const theme = useTheme();
  const { surface, surfaceHover } = getSurfaceTokens(theme);

  return (
    <Box
      key={tutorialVideo.id}
      role="button"
      onClick={() => handleSelectVideo(tutorialVideo)}
      sx={{
        display: "flex",
        minWidth: "300px",
        flexDirection: "column",
        gap: "8px",
        padding: "12px",
        border:
          selectedVideo.id === tutorialVideo.id
            ? "1px solid #E2772E"
            : "1px solid #5B6D7A40",
        borderRadius: "4px",
        cursor: "pointer",
        backgroundColor:
          selectedVideo.id === tutorialVideo.id
            ? "#E2772E20"
            : theme.palette.mode === "dark"
              ? "#4D5063"
              : surface,

        "&:hover": {
          backgroundColor:
            selectedVideo.id === tutorialVideo.id
              ? "#E2772E20"
              : theme.palette.mode === "dark"
                ? surfaceHover
                : "#5B6D7A25",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          borderRadius: "4px",
          height: "160px",
          border:
            selectedVideo.id === tutorialVideo.id
              ? "1px solid #E2772E"
              : "1px solid #5B6D7A40",
          overflow: "hidden",
          position: "relative",
        }}
      >
        <Box
          sx={{
            display: "flex",
            backgroundColor: "#14181FB2",
            padding: "4px 8px",
            color: "#FFF",
            position: "absolute",
            borderRadius: "4px",
            bottom: "8px",
            right: "8px",
          }}
        >
          <Typography sx={{ fontSize: "12px" }}>
            {currentLang === "fr"
              ? tutorialVideo.frLength
              : tutorialVideo.length}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
            height: "100%",
            color: "#FFF",
            position: "absolute",
            borderRadius: "4px",
            opacity: 0,

            "&:hover": {
              opacity: 100,
              backgroundColor: "#00000030",
            },
          }}
        >
          <PlayIcon
            height={`${UI_SIZES.iconButtonSize}px`}
            width={`${UI_SIZES.iconButtonSize}px`}
          />
        </Box>
        <Box
          component="img"
          src={
            currentLang === "fr"
              ? tutorialVideo.frThumbnail
              : tutorialVideo.thumbnail
          }
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
        />
      </Box>

      <Typography sx={{ fontWeight: 600, fontSize: "14px" }}>
        {t(tutorialVideo.name)}
      </Typography>
    </Box>
  );
};

export default VideoCard;
